import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

import usePlanHandle from 'hooks/billing/usePlanHandle';
import { useAppSelector } from 'redux/hooks';
import { currentEcomSelector } from 'redux/slices/ecoms-slice';

import { DefaultGTMProps } from 'features/google-tag-manager/model/default-gtm-props';
import { GtmAction } from 'features/google-tag-manager/model/gtm-action';
import { sendGTMEvent } from 'features/google-tag-manager/utils/send-gtm-event';

type SendGTMAction = (action: GtmAction) => Promise<boolean>;

const useSendGTMAction = (): SendGTMAction => {
    const currentEcom = useAppSelector(currentEcomSelector);

    const { planHandle, isLoading: subscriptionLoading } = usePlanHandle();

    const { pathname } = useLocation();

    const defaultGTMProps = useMemo((): DefaultGTMProps | null => {
        // subscriptionLoading is true from app start until both the Shopify plans
        // and the current subscription have resolved; legacy subscriptions end up
        // with planHandle undefined after loading, which is a real value to send.
        if (!currentEcom?.domain || subscriptionLoading) return null;

        return {
            domain: currentEcom.domain,
            plan_handle: planHandle,
            pathname,
        };
    }, [currentEcom?.domain, planHandle, subscriptionLoading, pathname]);

    return useCallback(
        (action: GtmAction): Promise<boolean> => {
            if (!defaultGTMProps) return Promise.resolve(false);

            sendGTMEvent({
                event: action.event,
                ...defaultGTMProps,
                ...action.payload,
            });

            return Promise.resolve(true);
        },
        [defaultGTMProps]
    );
};

export default useSendGTMAction;
